import React, { useEffect, useState, useCallback } from 'react'
import {
  ProductDownloadBreakdownRow,
  ProductDownloadBreakdownDto,
} from '@monorepo/interfaces'
import { http } from '@monorepo/infra'
import { format } from 'date-fns'
import * as S from '../styles'
import { PageSpinner } from '@monorepo/components'
import * as FileSaver from 'file-saver'
import XLSX from 'sheetjs-style'

interface ProductDownloadBreakdownProps {
  channel: string
  startDate: Date
  endDate: Date
}

const ProductDownloadBreakdown: React.FC<ProductDownloadBreakdownProps> = ({
  channel,
  startDate,
  endDate,
}) => {
  const [report, setReport] = useState<ProductDownloadBreakdownDto | null>(null)
  useEffect(() => {
    http
      .get<ProductDownloadBreakdownDto>({
        // Send a plain calendar date, exactly as Products Ordered does. This
        // used to send local time with the viewer's offset, which shifted the
        // window and made the same "July" cover a different period than
        // Products Ordered. The backend normalises either form now, but keeping
        // the two callers identical stops the asymmetry coming back.
        url: `/reports/productDownloadBreakdown?channel=${channel}&dateFrom=${format(
          startDate,
          'yyyy-MM-dd'
        )}&dateTo=${format(endDate, 'yyyy-MM-dd')}`,
      })
      .then(({ data }) => setReport(data))
  }, [channel, startDate, endDate])
  const exportExcel = useCallback(async () => {
    if (Array.isArray(report?.products)) {
      const reportData = report?.products.map((reportRow) => {
        return {
          SKU: reportRow.sku,
          Name: reportRow.name,
          // One row per variant, so without Type these look like duplicates
          // and anyone de-duplicating the sheet loses volume.
          Type: reportRow.type,
          Total: reportRow.total,
        }
      })
      const reportDataActivityByDealer = Array.isArray(report?.activityByDealer)
        ? report?.activityByDealer.map((reportRow) => {
            return {
              SKU: reportRow.sku,
              Name: reportRow.name,
              'Media Id': reportRow.mediaId,
              Channel: reportRow.channel,
              'Order Number': reportRow.orderNumber,
              Date: format(new Date(reportRow.date), 'MM/dd/yyyy'),
              'Dealer Id': reportRow.dealerId,
              'Team Member': reportRow.teamMember,
              'Team Member Email': reportRow.teamMemberEmail,
            }
          })
        : undefined
      const reportDataInactiveProducts = Array.isArray(report?.inactiveProducts)
        ? report?.inactiveProducts.map((reportRow) => {
            return {
              SKU: reportRow.sku,
              Name: reportRow.name,
            }
          })
        : undefined
      const fileType =
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset-UTF-8'
      const fileExtension = '.xlsx'
      const ws = XLSX.utils.json_to_sheet(reportData as unknown[])
      let wsActivityByDealer
      if (reportDataActivityByDealer) {
        wsActivityByDealer = XLSX.utils.json_to_sheet(
          reportDataActivityByDealer as unknown[]
        )
      }
      let wsInactiveProducts
      if (reportDataInactiveProducts) {
        wsInactiveProducts = XLSX.utils.json_to_sheet(
          reportDataInactiveProducts as unknown[]
        )
      }
      const wb = {
        Sheets: {
          Products: ws,
          ...(wsActivityByDealer && { Activity: wsActivityByDealer }),
          ...(wsInactiveProducts && {
            'Inactive products': wsInactiveProducts,
          }),
        },
        SheetNames: [
          'Products',
          ...(wsActivityByDealer ? ['Activity'] : []),
          ...(wsInactiveProducts ? ['Inactive products'] : []),
        ],
      }
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const data = new Blob([excelBuffer], { type: fileType })
      FileSaver.saveAs(data, `DigitalDownloads${fileExtension}`)
    }
  }, [report])
  if (!report || report === null) {
    return <PageSpinner />
  }

  return (
    <S.Container>
      <S.ReportTitle>
        Digital Downloads
        <S.ExcelButton
          label="Download Excel"
          colorOption="blue"
          onClick={exportExcel}
        />
      </S.ReportTitle>
      <S.FilterList>
        <S.FilterListItem>
          <S.FilterListItemTitle>Channel</S.FilterListItemTitle>
          <S.FilterListItemValue>{channel.toUpperCase()}</S.FilterListItemValue>
          <S.StyledIcon icon="FourPlusRectangle" />
        </S.FilterListItem>
        <S.FilterListItem>
          <S.FilterListItemTitle>Time Period</S.FilterListItemTitle>
          <S.FilterListItemValue>
            {`${format(startDate, 'MM/dd/yyyy')} - ${format(
              endDate,
              'MM/dd/yyyy'
            )}`}
          </S.FilterListItemValue>
          <S.StyledIcon icon="FourPlusRectangle" />
        </S.FilterListItem>
      </S.FilterList>
      {report?.products?.length > 0 && (
        <>
          <S.TableContainer>
            <S.Table>
              <S.TableHead>
                <S.TableRow>
                  <S.TableHeadCell>Product Image</S.TableHeadCell>
                  <S.TableHeadCell>Product SKU</S.TableHeadCell>
                  <S.TableHeadCell>Product Name</S.TableHeadCell>
                  <S.TableHeadCell>Type</S.TableHeadCell>
                  <S.TableHeadCell>Total Downloads</S.TableHeadCell>
                </S.TableRow>
              </S.TableHead>
              <S.TableBody>
                {report?.products?.map(
                  (item: ProductDownloadBreakdownRow, index: number) => (
                    <S.TableRow key={`products${index}`}>
                      <S.TableCell>
                        <S.TableImageContainer>
                          <S.TableImage src={item.image} />
                        </S.TableImageContainer>
                      </S.TableCell>
                      <S.TableCell>{item.sku}</S.TableCell>
                      <S.TableCell>{item.name}</S.TableCell>
                      <S.TableCell>{item.type}</S.TableCell>
                      <S.TableCell>{`${item.total}`}</S.TableCell>
                    </S.TableRow>
                  )
                )}
              </S.TableBody>
            </S.Table>
          </S.TableContainer>
        </>
      )}
    </S.Container>
  )
}

export default ProductDownloadBreakdown
